import {
  Box,
  CircularProgress,
  Skeleton,
  Stack,
  Theme,
  Typography,
  useMediaQuery,
} from "@mui/material";
import {
  getFormattedStatsCount,
  handleErrorMessage,
  isTruthy,
} from "helpers/methods";
import { useEffect, useState } from "react";
import { selectId } from "redux/authSlice";
import { selectIsCampaignSubmitted } from "redux/generalSlice";
import { getEmailCountById } from "screens/Settings/Users/UsersService";
import { useAppSelector } from "utils/hooks";

const EmailUsageChart = () => {
  const smUp = useMediaQuery((theme: Theme) => theme.breakpoints.up("sm"));
  const [loading, setLoading] = useState<boolean>(false);
  const [emailLimit, setEmailLimit] = useState({
    entity: "email",
    allowedEmailCount: 0,
    emailCount: 0,
  });
  const userId = useAppSelector(selectId);
  const isCampaignSubmitted = useAppSelector(selectIsCampaignSubmitted);

  useEffect(() => {
    getEmailUsageCount();
  }, [isCampaignSubmitted]);

  const getEmailUsageCount = async () => {
    try {
      setLoading(true);
      const response = await getEmailCountById(userId);
      setEmailLimit(response);
      setLoading(false);
    } catch (error: any) {
      setLoading(false);
      handleErrorMessage(error);
    } finally {
      setLoading(false);
    }
  };

  const getPercentage = () => {
    const current = emailLimit.emailCount || 0;
    const limit =
      emailLimit.allowedEmailCount === 0 ? 1 : emailLimit.allowedEmailCount;
    return isTruthy(emailLimit) && ((current / limit) * 100).toFixed();
  };

  return isTruthy(emailLimit) && emailLimit?.allowedEmailCount !== 0 ? (
    <Stack
      display="flex"
      alignItems="center"
      direction="row"
      spacing={1.5}
      justifyContent="center"
      pl={smUp ? 1 : 0}
    >
      <Box position="relative" display="inline-flex" mr={2}>
        <CircularProgress
          variant="determinate"
          value={100}
          size={40}
          sx={{
            position: "absolute",
            color: "#EBEBEB",
          }}
          thickness={8}
        />
        <CircularProgress
          variant="determinate"
          value={isTruthy(emailLimit) ? +getPercentage() : 0}
          size={40}
          sx={{
            color: "#7551D0",
          }}
          thickness={8}
        />
      </Box>

      <Box>
        <Typography
          variant="body1"
          fontWeight="bold"
          color="#7551D0"
          component="span"
        >
          {getFormattedStatsCount(emailLimit.emailCount! ?? 0)} emails used
        </Typography>
        <Typography
          variant="body1"
          color="#475569"
          component="span"
          style={{ marginLeft: "4px" }}
        >
          from {getFormattedStatsCount(emailLimit.allowedEmailCount ?? 0)}
        </Typography>
      </Box>
    </Stack>
  ) : loading ? (
    <Skeleton variant="rounded" width={200} height={40} />
  ) : null;
};

export default EmailUsageChart;
